import {
  LOCALSTORAGE_DELIVERY_BAR_PERSISTED_STATE,
  LOCALSTORAGE_TOKEN_KEY,
} from '@constants/localStorage';
import Cookies from 'universal-cookie';
import { IAuthCRUD } from '@monofront/business/interfaces';
import {
  EmailAuthProvider,
  User,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  getAuth,
  onIdTokenChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  updateEmail,
  updatePassword,
  signInWithCustomToken,
} from '@firebase/auth';
import firebaseService from '@services/firebase';
import logger from '@utils/logger';

export class AuthCRUD implements IAuthCRUD {
  private cookies: any;
  private authUser: User | null = null;

  constructor() {
    this.cookies = new Cookies(['lfAuth', 'lfABAuth', 'lfABIsMember']);
  }

  private clearStorage() {
    localStorage.removeItem(LOCALSTORAGE_TOKEN_KEY);
    localStorage.removeItem(LOCALSTORAGE_DELIVERY_BAR_PERSISTED_STATE);

    this.cookies.remove('lfAuth', {
      path: '/',
    });

    this.cookies.set('lfABAuth', 'false', {
      expires: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
      path: '/',
    });

    this.cookies.set('lfABIsMember', 'false', {
      expires: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
      path: '/',
    });
  }

  private setStorage(member, token) {
    localStorage.setItem(LOCALSTORAGE_TOKEN_KEY, token);

    this.cookies.set('lfAuth', 'true', {
      expires: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
      path: '/',
    });

    this.cookies.set('lfABAuth', 'true', {
      expires: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
      path: '/',
    });

    this.cookies.set('lfABIsMember', member ? 'true' : 'false', {
      expires: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
      path: '/',
    });
  }

  public async listen(authenticatedCallback, unauthenticatedCallback) {
    // Handle auth token from React Native Webview the soonest possible.
    // Why we retry: this sign-in intermittently hits a transient
    // `auth/network-request-failed` in the app webview. We retry it a few
    // times, and — success or not — it must never throw: a throw here skips
    // the onIdTokenChanged registration below, which left every auth-gated
    // page blank until the app was reinstalled.
    if (typeof window !== 'undefined' && window._lfFirebaseToken) {
      const MAX_SIGN_IN_ATTEMPTS = 3;

      for (let attempt = 0; attempt < MAX_SIGN_IN_ATTEMPTS; attempt++) {
        try {
          await signInWithCustomToken(
            getAuth(firebaseService.app),
            window._lfFirebaseToken,
          );
          break;
        } catch (error) {
          if (attempt === MAX_SIGN_IN_ATTEMPTS - 1) {
            logger.error('Webview custom-token sign-in failed after retries', {
              error: String(error),
            });
          } else {
            logger.warn('Webview custom-token sign-in failed, retrying', {
              attempt: String(attempt + 1),
              error: String(error),
            });
            await new Promise((resolve) =>
              setTimeout(resolve, 300 * (attempt + 1)),
            );
          }
        }
      }

      delete window._lfFirebaseToken;
    }

    return onIdTokenChanged(getAuth(), async (authUser) => {
      this.authUser = authUser;

      if (!authUser) {
        this.clearStorage();
        unauthenticatedCallback();
      } else {
        const resultTokenId = await authUser.getIdTokenResult();
        const claims = resultTokenId.claims;
        const member = claims.member;
        const token = resultTokenId.token;

        this.setStorage(member, token);

        authenticatedCallback({
          authUser,
          token,
          isMember: member,
          isFromSocialMedia: !authUser?.providerData.some(
            (provider) => provider?.providerId === 'password',
          ),
        });
      }
    });
  }

  public forceUpdate() {
    if (this.authUser) {
      this.authUser.getIdTokenResult(true);
    }
  }

  public async update(data) {
    if (data.newPassword) {
      const user = getAuth().currentUser!;
      const cred = EmailAuthProvider.credential(
        data.email,
        data.currentPassword,
      );

      await reauthenticateWithCredential(user, cred);

      return updatePassword(user, data.newPassword);
    } else if (data.newEmail && this.authUser) {
      return updateEmail(this.authUser, data.newEmail);
    } else {
      throw new Error('cannot update');
    }
  }

  public get uid() {
    return this.authUser?.uid;
  }

  public sendPasswordResetEmail(email: string) {
    return sendPasswordResetEmail(getAuth(), email);
  }

  public async createUser(email: string, password: string) {
    const credentials = await createUserWithEmailAndPassword(
      getAuth(),
      email,
      password,
    );

    const additionalUserInfo = getAdditionalUserInfo(credentials);

    return { credentials, additionalUserInfo };
  }
}
